import { db } from '../firebase';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  DocumentData,
  WithFieldValue
} from 'firebase/firestore';
import { AuditLogger } from './databaseMigration';

// Base interface for all entities with common fields
export interface BaseEntity {
  id: string;
  createdAt?: any;
  updatedAt?: any;
}

// Common CRUD operations helper class
export class CRUDHelper {
  /**
   * Create a new document with automatic timestamps and audit logging
   */
  static async create<T extends WithFieldValue<DocumentData>>(
    collectionName: string,
    data: T,
    userId?: string,
    userEmail?: string
  ): Promise<string> {
    try {
      const docRef = await addDoc(collection(db, collectionName), {
        ...data,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Log creation if user info provided
      if (userId && userEmail) {
        await AuditLogger.logAction(
          userId,
          userEmail,
          'CREATE',
          collectionName,
          docRef.id,
          {
            before: {},
            after: data,
            fieldsChanged: Object.keys(data as any)
          }
        );
      }

      return docRef.id;
    } catch (error) {
      console.error(`Error creating ${collectionName}:`, error);
      throw error;
    }
  }

  /**
   * Update an existing document with automatic timestamps and audit logging
   */
  static async update<T extends Partial<WithFieldValue<DocumentData>>>(
    collectionName: string,
    documentId: string,
    updates: T,
    userId?: string,
    userEmail?: string,
    previousData?: any
  ): Promise<void> {
    try {
      const docRef = doc(db, collectionName, documentId);
      const updateData = {
        ...updates,
        updatedAt: serverTimestamp()
      };

      await updateDoc(docRef, updateData);

      // Log update if user info provided
      if (userId && userEmail) {
        await AuditLogger.logAction(
          userId,
          userEmail,
          'UPDATE',
          collectionName,
          documentId,
          {
            before: previousData || {},
            after: updateData,
            fieldsChanged: Object.keys(updates as any)
          }
        );
      }
    } catch (error) {
      console.error(`Error updating ${collectionName}/${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a document with audit logging
   */
  static async delete(
    collectionName: string,
    documentId: string,
    userId?: string,
    userEmail?: string,
    documentData?: any
  ): Promise<void> {
    try {
      await deleteDoc(doc(db, collectionName, documentId));

      // Log deletion if user info provided
      if (userId && userEmail) {
        await AuditLogger.logAction(
          userId,
          userEmail,
          'DELETE',
          collectionName,
          documentId,
          {
            before: documentData || {},
            after: {},
            fieldsChanged: ['deleted']
          }
        );
      }
    } catch (error) {
      console.error(`Error deleting ${collectionName}/${documentId}:`, error);
      throw error;
    }
  }

  /**
   * Toggle boolean field (active/inactive, enabled/disabled, etc.)
   */
  static async toggleBoolean(
    collectionName: string,
    documentId: string,
    fieldName: string,
    currentValue: boolean,
    userId?: string,
    userEmail?: string
  ): Promise<void> {
    const updates = { [fieldName]: !currentValue };
    await this.update(collectionName, documentId, updates, userId, userEmail, { [fieldName]: currentValue });
  }
}

// Standard form validation helpers
export class ValidationHelper {
  static required(value: any, fieldName: string): string | null {
    if (!value || (typeof value === 'string' && value.trim() === '')) {
      return `${fieldName} es requerido`;
    }
    return null;
  }

  static email(value: string): string | null {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (value && !emailRegex.test(value)) {
      return 'Email inválido';
    }
    return null;
  }

  static minLength(value: string, min: number, fieldName: string): string | null {
    if (value && value.length < min) {
      return `${fieldName} debe tener al menos ${min} caracteres`;
    }
    return null;
  }

  static numeric(value: any, fieldName: string): string | null {
    if (value && isNaN(Number(value))) {
      return `${fieldName} debe ser un número`;
    }
    return null;
  }

  static validateForm(validations: (() => string | null)[]): string[] {
    return validations
      .map(validation => validation())
      .filter(error => error !== null) as string[];
  }
}

// UI consistency helpers
export class UIHelper {
  static getStandardButtonStyle(variant: 'primary' | 'secondary' | 'danger' | 'success' = 'primary') {
    const baseStyle = {
      padding: '8px 16px',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '500'
    };

    const variants = {
      primary: { backgroundColor: '#2196F3', color: 'white' },
      secondary: { backgroundColor: '#f5f5f5', color: '#333' },
      danger: { backgroundColor: '#f44336', color: 'white' },
      success: { backgroundColor: '#4CAF50', color: 'white' }
    };

    return { ...baseStyle, ...variants[variant] };
  }

  static getStandardInputStyle() {
    return {
      padding: '8px',
      border: '1px solid #ddd',
      borderRadius: '4px',
      fontSize: '14px'
    };
  }

  static getStandardTableStyle() {
    return {
      width: '100%',
      borderCollapse: 'collapse' as const,
      fontSize: '14px'
    };
  }

  static getStandardTableCellStyle(isHeader: boolean = false) {
    return {
      border: '1px solid #ddd',
      padding: '8px',
      textAlign: 'left' as const,
      backgroundColor: isHeader ? '#f5f5f5' : 'transparent'
    };
  }

  static getStandardCardStyle() {
    return {
      marginBottom: '40px',
      border: '1px solid #ddd',
      padding: '20px',
      borderRadius: '8px'
    };
  }
}

// Status and state management helpers
export class StatusHelper {
  static getStatusIcon(isActive: boolean): string {
    return isActive ? '✅' : '❌';
  }

  static getStatusText(isActive: boolean, activeText: string = 'Activo', inactiveText: string = 'Inactivo'): string {
    return isActive ? activeText : inactiveText;
  }

  static getToggleButtonText(isActive: boolean, activateText: string = 'Activar', deactivateText: string = 'Desactivar'): string {
    return isActive ? deactivateText : activateText;
  }
}